/**
 * DraftFeedbackView — Version Compare's "Draft Feedback" tab for the person a private draft is shared with (wireframe
 * jobs-library-ownership-v3.html cmpView; Ownership design, part 3). The approval review's side-by-side smart-diff
 * (SmartDiffViewerV3) of the latest draft against the version it grew from: clicking a changed sentence adds a
 * comment, held "not submitted yet" (seen only by its author) until Submit comments (top right), which saves them,
 * tells the owner and closes the view. Submitted comments stack per sentence: anyone shared replies to a comment or
 * adds their own; authors edit or delete their own (a delete takes its replies) until the holder resolves them. A
 * comment not submitted yet is edited in place or removed.
 *   jobId, ownerName
 *   onPendingChange(count) — the unsubmitted comments (the page asks before closing with some)
 *   onCountChange(count) — the submitted comments on the draft (the tab shows it)
 *   onDone() — submitted: close the view
 *   onRefused({ message }) — the draft changed, was sent or the share ended: the page reloads
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { SbButton, SbNotification, SbSpin, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import SbInput from 'uiCore/SbInput/SbInput';
import useAuthStore from 'Store/useAuthStore';
import {
  deleteDraftFeedback, editDraftFeedback, getDraftDiff, getDraftFeedback, submitDraftFeedback,
} from 'Api/apiService';
import SmartDiffViewerV3 from 'Components/SbDiffCheckerV3/SmartDiffViewerV3';
import { VIEW_MODE } from 'constants/jobs.constants';
import {
  ADD_COMMENT_LABEL, ADD_REPLY_LABEL, COMMENT_ADDED_TOAST, COMMENT_PLACEHOLDER, EDITED_LABEL, EDIT_PLACEHOLDER,
  INVALID_COMMENT_LABEL, MAX_COMMENT_LENGTH, NOT_SUBMITTED_LABEL, REPLY_PLACEHOLDER,
  SUBMIT_COMMENTS_LABEL, TOOLTIP_SUBMIT_DISABLED, TOO_MANY_COMMENTS, getCommentsSentToast, getDeleteConfirm,
  getFeedbackError, getFeedbackNote, getThreads, isCommentableItem, isOwnComment, withPendingComment,
} from 'Utils/draftFeedback';
import {
  CommentActionStyled, CommentBoxStyled, CommentCardStyled, CommentMetaStyled, FeedbackBarStyled, FeedbackNoteStyled,
  ThreadsStyled,
} from './DraftFeedback.styles';

let tempIds = 0;
const nextTempId = () => { tempIds += 1; return `pending-${tempIds}`; };
const formatWhen = (iso) => {
  const when = iso ? new Date(iso) : null;
  return when && !Number.isNaN(when.getTime())
    ? when.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};

// A small text box with Cancel / confirm (adding a comment or a reply, editing one's own)
const CommentInput = ({ placeholder, confirmLabel, initial = '', onCancel, onConfirm }) => {
  const [text, setText] = useState(initial);
  return (
    <CommentBoxStyled>
      <SbInput textarea rows={2} placeholder={placeholder} value={text} maxLength={MAX_COMMENT_LENGTH}
        onChange={(e) => setText(e.target.value)} />
      <div>
        <SbButton size="small" onClick={onCancel}>Cancel</SbButton>
        <SbButton size="small" buttonType="primary" disabled={!text.trim()} onClick={() => onConfirm(text.trim())}>
          {confirmLabel}
        </SbButton>
      </div>
    </CommentBoxStyled>
  );
};

CommentInput.propTypes = {
  placeholder: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  initial: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const DraftFeedbackView = ({
  jobId, ownerName = '', onPendingChange = undefined, onCountChange = undefined, onDone, onRefused = undefined,
}) => {
  const email = useAuthStore((s) => s.email);
  const [diffData, setDiffData] = useState(null);
  const [comments, setComments] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);

  const refuse = (err) => {
    const failure = getFeedbackError(err);
    SbNotification.error('Comments not saved', failure.message);
    if (failure.reload && onRefused) onRefused(failure);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getDraftDiff(jobId), getDraftFeedback(jobId)])
      .then(([diff, list]) => {
        if (cancelled) return;
        setDiffData(diff);
        setComments(list?.comments || []);
      })
      .catch((err) => { if (!cancelled) refuse(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps -- refuse: the parent's latest is enough

  useEffect(() => { if (onPendingChange) onPendingChange(pending.length); }, [pending.length]); // eslint-disable-line react-hooks/exhaustive-deps -- once per change
  useEffect(() => { if (onCountChange) onCountChange(comments.length); }, [comments.length]); // eslint-disable-line react-hooks/exhaustive-deps -- once per change

  const addPending = (comment) => {
    const next = withPendingComment(pending, { ...comment, feedback_id: nextTempId(), author_email: email });
    if (!next) {
      SbNotification.warning(TOO_MANY_COMMENTS);
      return;
    }
    setPending(next);
    SbNotification.info(COMMENT_ADDED_TOAST);
  };

  const run = async (action, onRefusal = undefined) => {
    setBusy(true);
    try {
      const result = await action();
      setComments(result?.comments || []);
      return true;
    } catch (err) {
      if (onRefusal) onRefusal(err);
      refuse(err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    const toSend = pending.map(({ anchor_text: anchor, text, reply_to: parent }) => (
      parent ? { text, reply_to: parent } : { anchor_text: anchor, text }));
    const count = toSend.length;
    // the server names the comments it refused (e.g. a sentence that is no longer a change): mark them
    const markRefused = (err) => {
      const refused = new Set(err?.response?.data?.invalid_comments || []);
      if (refused.size) setPending((prev) => prev.map((p, i) => ({ ...p, invalid: refused.has(i) })));
    };
    if (await run(() => submitDraftFeedback(jobId, toSend), markRefused)) {
      setPending([]);
      SbNotification.success(getCommentsSentToast(count, ownerName));
      onDone();
    }
  };

  const handleDelete = (comment, hasReplies) => {
    Modal.confirm({
      ...getDeleteConfirm(hasReplies),
      onOk: () => run(() => deleteDraftFeedback(comment.feedback_id)),
    });
  };

  const card = (comment, isReply, anchor) => {
    const mine = isOwnComment(comment, email);
    const editable = mine && !comment.pending;
    if (editing === comment.feedback_id) {
      return (
        <CommentInput
          key={comment.feedback_id}
          placeholder={EDIT_PLACEHOLDER}
          confirmLabel="Save"
          initial={comment.text}
          onCancel={() => setEditing(null)}
          onConfirm={async (text) => {
            // a comment not submitted yet is only held here: edit it in place (the server never saw it)
            if (comment.pending) {
              setPending((prev) => prev.map((p) => (p.feedback_id === comment.feedback_id
                ? { ...p, text, invalid: false } : p)));
              setEditing(null);
            } else if (await run(() => editDraftFeedback(comment.feedback_id, text))) {
              setEditing(null);
            }
          }}
        />
      );
    }
    return (
      <CommentCardStyled key={comment.feedback_id} $pending={comment.pending} $reply={isReply}
        data-testid="feedback-comment">
        <CommentMetaStyled>
          {isReply && '↳ '}
          <b>{comment.pending ? 'You' : comment.author_name || comment.author_email}</b>
          {' · '}
          {comment.pending ? NOT_SUBMITTED_LABEL : formatWhen(comment.created_at)}
          {comment.edited_at && ` · ${EDITED_LABEL}`}
          {!comment.pending && !isReply && (
            <CommentActionStyled type="button" disabled={busy} onClick={() => setReplyTo({ id: comment.feedback_id, anchor })}>
              Reply
            </CommentActionStyled>
          )}
          {editable && (
            <>
              <CommentActionStyled type="button" disabled={busy} onClick={() => setEditing(comment.feedback_id)}>
                Edit
              </CommentActionStyled>
              <CommentActionStyled type="button" disabled={busy}
                onClick={() => handleDelete(comment, !isReply && comments.some((r) => r.reply_to === comment.feedback_id))}>
                Delete
              </CommentActionStyled>
            </>
          )}
          {comment.pending && (
            <>
              <CommentActionStyled type="button" disabled={busy} onClick={() => setEditing(comment.feedback_id)}>
                Edit
              </CommentActionStyled>
              <CommentActionStyled type="button"
                onClick={() => setPending((prev) => prev.filter((p) => p.feedback_id !== comment.feedback_id))}>
                Remove
              </CommentActionStyled>
            </>
          )}
        </CommentMetaStyled>
        <div>{comment.text}</div>
        {comment.invalid && <CommentMetaStyled data-testid="invalid-comment">{INVALID_COMMENT_LABEL}</CommentMetaStyled>}
      </CommentCardStyled>
    );
  };

  const feedback = {
    isCommentable: isCommentableItem,
    popoverFor: (item, close) => (
      <CommentInput
        placeholder={COMMENT_PLACEHOLDER}
        confirmLabel={ADD_COMMENT_LABEL}
        onCancel={close}
        onConfirm={(text) => { addPending({ anchor_text: item.text, text }); close(); }}
      />
    ),
    threadsFor: (item) => {
      const threads = getThreads(comments, item.text, pending);
      if (!threads.length) return null;
      return (
        <ThreadsStyled>
          {threads.map(({ root, replies }) => (
            <React.Fragment key={root.feedback_id}>
              {card(root, false, item.text)}
              {replies.map((r) => card(r, true, item.text))}
              {replyTo?.id === root.feedback_id && (
                <CommentInput
                  placeholder={REPLY_PLACEHOLDER}
                  confirmLabel={ADD_REPLY_LABEL}
                  onCancel={() => setReplyTo(null)}
                  onConfirm={(text) => {
                    addPending({ anchor_text: replyTo.anchor, text, reply_to: root.feedback_id });
                    setReplyTo(null);
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </ThreadsStyled>
      );
    },
  };

  if (loading) return <SbSpin size="large" />;
  const viewData = diffData?.diff ? { ...diffData, left_pane: diffData.diff.left_pane || [],
    right_pane: diffData.diff.right_pane || [] } : null;

  return (
    <>
      <FeedbackBarStyled>
        <FeedbackNoteStyled>{getFeedbackNote(ownerName)}</FeedbackNoteStyled>
        <SbTooltip content={TOOLTIP_SUBMIT_DISABLED} disabled={pending.length > 0}>
          <TooltipTriggerStyled>
            <SbButton buttonType="primary" loading={busy} disabled={!pending.length || busy} onClick={handleSubmit}>
              {SUBMIT_COMMENTS_LABEL}{pending.length ? ` (${pending.length})` : ''}
            </SbButton>
          </TooltipTriggerStyled>
        </SbTooltip>
      </FeedbackBarStyled>
      {viewData && <SmartDiffViewerV3 diffData={viewData} viewMode={VIEW_MODE.FEEDBACK} readOnly feedback={feedback} />}
    </>
  );
};

DraftFeedbackView.propTypes = {
  jobId: PropTypes.string.isRequired,
  ownerName: PropTypes.string,
  onPendingChange: PropTypes.func,
  onCountChange: PropTypes.func,
  onDone: PropTypes.func.isRequired,
  onRefused: PropTypes.func,
};

export default DraftFeedbackView;
